import React, { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Button, DatePicker, Form, InputNumber, Select, Tag, Tooltip, Typography } from "antd";
import { CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { DATE_TIME_FORMAT, TIME_INPUT_FORMAT } from "../drinkStats/constants";
import { AMOUNT_PRESETS, DURATION_PRESETS, AmountPresetKey, useAddDrink } from "./useAddDrink";
import { QuickAddList } from "./QuickAddList";
import { toAbvPercent } from "../../../services/drinks/utils/numbers";

type AddDrinkState = ReturnType<typeof useAddDrink>;
type WhenChoice = "now" | "15" | "30" | "custom";

export const AddDrinkContent: React.FC<{ state: AddDrinkState }> = ({ state }) => {
    const {
        t, embedded, drinks, fetching, drinksFromCache, drinksError, recentTemplatesError, isOnline, pendingCount, failedCount,
        handleSubmit, drinkOptions, selectedDrinkId, setSelectedDrinkId, handlePresetChange,
        selectedPreset, amountMl, handleAmountInputChange, consumedAt, setConsumedAt, setDurationMinutes,
        startAtOverride, setSelectedDurationPreset, durationMinutes, isMobile, setLockBodyScroll, popupParent,
        selectedDurationPreset, handleDurationPresetChange, handleDurationInputChange,
        selectedDurationMeta, submitting, handleCloseModal, recentTemplates,
    } = state;
    const [whenChoice, setWhenChoice] = useState<WhenChoice>("now");
    let status: string | null = null;
    if (failedCount > 0) status = t("addDrink.offline.pendingWithFailed", { count: pendingCount, failed: failedCount });
    else if (pendingCount > 0) status = t("addDrink.offline.pending", { count: pendingCount });
    else if (!isOnline) status = t("addDrink.offline.description");
    const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const selectedDrink = drinks.find((drink) => drink.id === selectedDrinkId);
    const durationLabel = selectedDurationPreset === "custom"
        ? t("addDrink.form.durationPresetMinutes", { minutes: durationMinutes })
        : selectedDurationMeta?.label ?? "";
    const whenLabel = whenChoice === "custom"
        ? (consumedAt?.format(DATE_TIME_FORMAT) ?? t("addDrink.when.custom"))
        : t(`addDrink.when.${whenChoice}`);
    const options = useMemo(() => {
        const recentCount = new Map(recentTemplates.map((template) => [template.drinkId, template.count]));
        return [...drinkOptions].sort((a, b) => (recentCount.get(Number(b.value)) ?? 0) - (recentCount.get(Number(a.value)) ?? 0));
    }, [drinkOptions, recentTemplates]);

    const chooseWhen = (choice: WhenChoice) => {
        setWhenChoice(choice);
        if (choice === "now") setConsumedAt(dayjs());
        if (choice === "15" || choice === "30") setConsumedAt(dayjs().subtract(Number(choice), "minute"));
    };

    return (
        <div className="add-drink-content">
            <header className="add-drink-heading">
                <Typography.Title level={embedded ? 2 : 1}>{t("addDrink.title")}</Typography.Title>
                <Button className="add-drink-close" type="text" aria-label={t("addDrink.close")} icon={<CloseOutlined />} onClick={handleCloseModal} />
            </header>
            {status && <output aria-live="polite" className="add-drink-status">{status}{isIos && <> {t("addDrink.offline.iosSyncHint")}</>}</output>}

            <QuickAddList state={state} />
            {isOnline && (drinksError || recentTemplatesError) && <div role="alert" className="add-drink-inline-error">{t("addDrink.errors.loadDrinks")}</div>}
            {!isOnline && drinks.length === 0 && <Typography.Text type="secondary">{t("addDrink.offline.noSavedDrinks")}</Typography.Text>}

            <Form layout="vertical" onFinish={() => handleSubmit(whenChoice === "now")} className="add-drink-form">
                <Form.Item label={t("addDrink.form.drinkLabel")} htmlFor="add-drink-drink" required>
                    <Select
                        id="add-drink-drink"
                        autoFocus
                        className="add-drink-drink-select"
                        placeholder={t("addDrink.form.drinkPlaceholder")}
                        options={options}
                        value={selectedDrinkId ?? undefined}
                        onChange={(value) => setSelectedDrinkId(typeof value === "number" ? value : null)}
                        loading={fetching}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        getPopupContainer={popupParent}
                        onOpenChange={(open) => { if (isMobile) setLockBodyScroll(open); }}
                    />
                    {drinksFromCache && drinks.length > 0 && <Tag className="add-drink-cache-tag">{t("addDrink.offline.savedDrinkList")}</Tag>}
                </Form.Item>

                <Form.Item label={t("addDrink.form.sizeLabel")}>
                    <fieldset className="add-drink-size-grid add-drink-choice-group" aria-label={t("addDrink.form.sizeLabel")}>
                        {Object.entries(AMOUNT_PRESETS).map(([key, preset]) => {
                            const value = key as AmountPresetKey;
                            return <Button
                                key={key}
                                className="add-drink-choice"
                                type={selectedPreset === value ? "primary" : "default"}
                                aria-pressed={selectedPreset === value}
                                onClick={() => handlePresetChange(value)}
                            >
                                <span className="add-drink-choice-name">{t(preset.labelKey)}</span>
                                <span className="add-drink-choice-value">{preset.amount == null ? t("addDrink.form.customSize") : `${preset.amount} ml`}</span>
                            </Button>;
                        })}
                    </fieldset>
                    {selectedPreset === "custom" && <InputNumber
                        className="add-drink-custom-amount"
                        min={1}
                        aria-label={t("common.units.ml")}
                        value={amountMl}
                        onChange={handleAmountInputChange}
                        addonAfter={t("common.units.ml")}
                    />}
                    {(amountMl > 1500 || (selectedDrink && toAbvPercent(selectedDrink.promille) > 96)) && <div role="status" aria-live="polite" className="add-drink-inline-warning">
                        {t("addDrink.form.implausibleEntry")}
                    </div>}
                </Form.Item>

                <Form.Item label={t("addDrink.form.whenLabel")}>
                    <fieldset className="add-drink-choice-row add-drink-when-row add-drink-choice-group" aria-label={t("addDrink.form.whenLabel")}>
                        {(["now", "15", "30", "custom"] as WhenChoice[]).map((choice) => <Button
                            key={choice}
                            className="add-drink-when-choice"
                            type={whenChoice === choice ? "primary" : "default"}
                            aria-pressed={whenChoice === choice}
                            onClick={() => chooseWhen(choice)}
                        >{t(`addDrink.when.${choice}`)}</Button>)}
                    </fieldset>
                    {whenChoice === "custom" && <DatePicker
                        className="add-drink-custom-time"
                        showTime={{ format: TIME_INPUT_FORMAT }}
                        format={DATE_TIME_FORMAT}
                        value={consumedAt}
                        onChange={(value: Dayjs | null) => {
                            setConsumedAt(value);
                            if (value && startAtOverride) {
                                setDurationMinutes(Math.max(0, value.diff(startAtOverride, "minute")));
                                setSelectedDurationPreset("custom");
                            }
                        }}
                        onOpenChange={(open) => { if (isMobile) setLockBodyScroll(open); }}
                        getPopupContainer={popupParent}
                    />}
                </Form.Item>

                <Form.Item label={<span className="add-drink-duration-label">{t("addDrink.form.durationLabel")} <Tooltip title={t("addDrink.form.durationTooltip")}><InfoCircleOutlined aria-label={t("addDrink.form.durationTooltip")} /></Tooltip></span>}>
                    <fieldset className="add-drink-choice-row add-drink-duration-row add-drink-choice-group" aria-label={t("addDrink.form.durationLabel")}>
                        {DURATION_PRESETS.filter(({ minutes }) => minutes !== 45).map((preset) => <Button
                            key={preset.minutes}
                            className="add-drink-duration-choice"
                            type={selectedDurationPreset === preset.minutes ? "primary" : "default"}
                            aria-pressed={selectedDurationPreset === preset.minutes}
                            onClick={() => handleDurationPresetChange(preset.minutes)}
                        >{t(preset.labelKey)}</Button>)}
                        <Button
                            className="add-drink-duration-choice"
                            type={selectedDurationPreset === "custom" ? "primary" : "default"}
                            aria-pressed={selectedDurationPreset === "custom"}
                            onClick={() => handleDurationPresetChange("custom")}
                        >{t("addDrink.form.durationCustom")}</Button>
                    </fieldset>
                    {selectedDurationPreset === "custom" && <InputNumber
                        className="add-drink-custom-duration"
                        min={0}
                        max={600}
                        aria-label={t("common.units.min")}
                        value={durationMinutes}
                        addonAfter={t("common.units.min")}
                        onChange={handleDurationInputChange}
                    />}
                </Form.Item>

                <div className="add-drink-footer">
                    <div id="add-drink-summary" className="add-drink-summary" aria-live="polite">
                        {selectedDrink ? t("addDrink.summary.line", {
                            drink: selectedDrink.name,
                            amount: amountMl,
                            when: whenLabel,
                            duration: durationLabel,
                        }) : t("addDrink.messages.selectDrink")}
                    </div>
                    <Button className="add-drink-submit" aria-describedby="add-drink-summary" type="primary" htmlType="submit" block size="large" loading={submitting} disabled={!selectedDrinkId || submitting}>
                        {t("addDrink.submitLabel")}
                    </Button>
                </div>
            </Form>
        </div>
    );
};
